import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector } from 'react-redux';
import PageWrapper from 'commons/src/components/containers/PageWrapper';
import WhiteSectionContainer from 'commons/src/components/containers/WhiteSectionContainer';
import { radonInsightPlaceholder } from 'commons/src/components/placeholder';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import { generateImage } from '../GraphConfig';
import { createObserverAndFetchSvg } from '../insightFunctions';
import IAQInsight2BuildingInfo from './IAQInsight2BuildingInfo';
import IAQInsight2Header from './IAQInsight2Header';
import IAQInsight2OpeningHours from './IAQInsight2OpeningHours';
import styles from './IAQInsight2ResultView.module.scss';
import IAQInsight2SensorMetrics from './IAQInsight2SensorMetrics';

export type IAQInsight2ResultViewProps = {
    from: Moment;
    to: Moment;
};

const IAQInsight2ResultView: React.FC<IAQInsight2ResultViewProps> = ({ from, to }) => {
    const { t: txt } = useTranslation();
    const [generateChartError, setChartError] = useState(false);

    const buildings = useSelector((state: Store) => state.buildings.buildings);
    const locationsLoading = useSelector((state: Store) => state.locations.loading);
    const loading = useSelector((state: Store) => state.requests[RequestType.FetchIaqInsightData]?.loading ?? false);
    const locationId = useSelector((state: Store) => state.iaqInsight.locationId);
    const sensorReports = useSelector((state: Store) => state.iaqInsight.sensorReports);
    const thresholds = useSelector((state: Store) => state.iaqInsight.thresholds);

    const numberOfDays = moment(to).diff(moment(from), 'days');
    const displayGraph = numberOfDays <= 31;

    const sensorReportsWithData = Object.keys(sensorReports).filter(sensor => {
        const report = sensorReports[sensor];
        return report.averageGraph ? report.averageGraph.length > 0 : false;
    });

    const getSvg = async (): Promise<void> => {
        const images: { data: string; serialNumber: string }[] = [];
        const updateListOfImages = (response: { data: string; serialNumber: string }): void => {
            images.push(response);
        };
        await sensorReportsWithData.forEach(sensor => {
            generateImage(updateListOfImages, setChartError, sensor);
        });
    };

    useEffect(() => {
        if (!loading) {
            const element = document.getElementById('resultHeader');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                const observer = createObserverAndFetchSvg('buildingResultSummary', getSvg);
                observer.observe(document, {
                    childList: true,
                    subtree: true,
                });
            }
        }
    }, [loading]);

    const building = locationId ? buildings[locationId] : undefined;

    return (
        <PageWrapper pageType="wide" whiteBackground>
            <ReactPlaceholder ready={!loading && !locationsLoading} customPlaceholder={radonInsightPlaceholder}>
                {building && (
                    <div className={styles.reportContainer}>
                        <IAQInsight2Header building={building} />
                        <IAQInsight2BuildingInfo building={building} />
                        <div className={styles.divider} />
                        <IAQInsight2OpeningHours building={building} />
                        <div className={styles.divider} />
                        <IAQInsight2SensorMetrics sensorReports={sensorReports} thresholds={thresholds} />
                    </div>
                )}
                {generateChartError && displayGraph && (
                    <WhiteSectionContainer>
                        <ResponseBox text="SomethingWentWrong" subtext={txt('RadonInsight.GeneratePdfError')} />
                    </WhiteSectionContainer>
                )}
            </ReactPlaceholder>
        </PageWrapper>
    );
};

export default IAQInsight2ResultView;
